export class SpeechManager {
  private static instance: SpeechManager;
  private enabled: boolean = true;
  private currentSpeed: number = 1.0;
  private currentAudio: HTMLAudioElement | null = null;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private currentText: string = '';
  private currentAudioUrl?: string;
  private speaking: boolean = false;
  private onEndCallbacks: Set<() => void> = new Set();

  private constructor() {}

  public static getInstance(): SpeechManager {
    if (!SpeechManager.instance) {
      SpeechManager.instance = new SpeechManager();
    }
    return SpeechManager.instance;
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.stop();
    }
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public isSpeaking(): boolean {
    const isBrowserSpeaking = typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis.speaking;
    const isAudioSpeaking = !!(this.currentAudio && !this.currentAudio.paused && !this.currentAudio.ended);
    return this.speaking || isBrowserSpeaking || isAudioSpeaking;
  }

  public setSpeed(speed: number): void {
    const newSpeed = Math.max(0.25, Math.min(4.0, speed));
    if (this.currentSpeed === newSpeed) return;
    this.currentSpeed = newSpeed;

    // Dynamically adjust audio element playback rate
    if (this.currentAudio) {
      this.currentAudio.playbackRate = this.currentSpeed;
    }

    // Dynamically update TTS speech rate if active
    if (this.speaking && this.currentText) {
      this.speak(this.currentText, this.currentAudioUrl);
    }
  }

  public getSpeed(): number {
    return this.currentSpeed;
  }

  public onEnd(callback: () => void): () => void {
    this.onEndCallbacks.add(callback);
    return () => {
      this.onEndCallbacks.delete(callback);
    };
  }

  private notifyEnd(): void {
    this.speaking = false;
    this.currentUtterance = null;
    const callbacks = Array.from(this.onEndCallbacks);
    this.onEndCallbacks.clear();
    callbacks.forEach((cb) => cb());
  }

  /**
   * Estimates duration in ms required to speak text cleanly, adjusted for current player speed
   */
  public estimateDurationMs(text: string): number {
    if (!text) return 1500;
    const words = text.replace(/^Teacher Voiceover:\s*/i, '').trim().split(/\s+/).length;
    // ~2.2 words per second plus 800ms natural pause
    const baseMs = Math.max(1500, Math.ceil((words / 2.2) * 1000 + 800));
    return Math.ceil(baseMs / this.currentSpeed);
  }

  public speak(text: string, audioUrl?: string, onComplete?: () => void): void {
    if (!this.enabled) {
      if (onComplete) onComplete();
      return;
    }

    this.stop(); // Stop prior audio/speech
    this.currentText = text;
    this.currentAudioUrl = audioUrl;

    if (onComplete) {
      const unbind = this.onEnd(() => {
        unbind();
        onComplete();
      });
    }

    if (audioUrl) {
      try {
        this.currentAudio = new Audio(audioUrl);
        this.currentAudio.playbackRate = this.currentSpeed;
        this.speaking = true;
        
        this.currentAudio.onended = () => {
          this.notifyEnd();
        };

        this.currentAudio.onerror = () => {
          this.currentAudio = null;
          this.speakTTS(text);
        };

        this.currentAudio.play().catch(() => {
          this.currentAudio = null;
          this.speakTTS(text);
        });
        return;
      } catch (err) {
        // Fallback to TTS
      }
    }

    this.speakTTS(text);
  }

  private speakTTS(text: string): void {
    if (!text || typeof window === 'undefined' || !('speechSynthesis' in window)) {
      this.notifyEnd();
      return;
    }

    try {
      window.speechSynthesis.cancel();
      const cleanText = text.replace(/^Teacher Voiceover:\s*/i, '').trim();
      if (!cleanText) {
        this.notifyEnd();
        return;
      }

      const utterance = new SpeechSynthesisUtterance(cleanText);
      // Adjust TTS rate dynamically with player speed (clamped between 0.5 and 3.0)
      const calculatedRate = Math.max(0.5, Math.min(3.0, 0.92 * this.currentSpeed));
      utterance.rate = calculatedRate;
      utterance.pitch = 1.0;
      utterance.lang = 'en-US';

      this.currentUtterance = utterance;
      this.speaking = true;

      utterance.onend = () => {
        this.notifyEnd();
      };

      utterance.onerror = () => {
        this.notifyEnd();
      };

      window.speechSynthesis.speak(utterance);
    } catch (err) {
      this.notifyEnd();
    }
  }

  public stop(): void {
    this.speaking = false;
    this.currentUtterance = null;
    this.currentText = '';
    this.currentAudioUrl = undefined;
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }
}
